/**
 * ezCater — organization Settings → Integrations.
 * Request activation (partner), ops credential entry, status badge, webhook URL.
 */
import {
  Alert,
  Button,
  Card,
  Form,
  Input,
  Space,
  Spin,
  Tag,
  Typography,
  message,
} from 'antd';
import { CopyOutlined } from '@ant-design/icons';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import {
  getEzCaterIntegration,
  requestEzCaterActivation,
  saveEzCaterCredentials,
  testEzCaterConnection,
} from 'api/ezcater';
import EzCaterLocationMapping from './EzCaterLocationMapping';
import {
  InfoTable,
  SectionTitle,
  formItemStyle,
  sectionCardProps,
  sectionStackGap,
} from './settingsShared';
import { AdminContext } from 'constants/contexts';
import { captureException } from 'utils/errors';

const { Text } = Typography;

const FORM_MAX_WIDTH = 480;

const STATUS_CONFIG = {
  not_configured: { color: 'default', label: 'Not configured' },
  pending: { color: 'processing', label: 'Pending' },
  active: { color: 'success', label: 'Active' },
  error: { color: 'error', label: 'Error' },
};

function copyToClipboard(text) {
  if (!text) return;
  navigator.clipboard.writeText(text).then(
    () => message.success('Copied to clipboard'),
    () => message.error('Could not copy'),
  );
}

function formatRestaurantLabel(restaurant) {
  const name = restaurant?.name || 'Unknown restaurant';
  const number = restaurant?.restaurant_number ?? restaurant?.id;
  if (number) {
    return `${name} (#${number})`;
  }
  return name;
}

function formatCatererLocationCounts(integration) {
  const parts = [];
  if (integration?.subscribed_caterer_count != null) {
    parts.push(`${integration.subscribed_caterer_count} subscribed`);
  }
  parts.push(`${integration?.mapped_caterer_count ?? 0} mapped`);
  parts.push(`${integration?.accessible_caterer_count ?? 0} accessible`);
  return parts.join(' / ');
}

function ezCaterDetail(statusKey) {
  if (statusKey === 'active') {
    return 'ezCater is active. Map locations below to receive catering orders.';
  }
  if (statusKey === 'pending') {
    return 'Activation is pending. Once you receive API credentials, enter the token below.';
  }
  if (statusKey === 'error') {
    return 'There is a problem with this ezCater integration. Review the error below.';
  }
  return 'Receive catering orders from ezCater and route delivery through FULFLLD when configured.';
}

function EzCaterIntegration() {
  const { organizationId } = useContext(AdminContext);
  const [loading, setLoading] = useState(false);
  const [integration, setIntegration] = useState(null);
  const [error, setError] = useState(null);
  const [requesting, setRequesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [form] = Form.useForm();

  const load = useCallback(() => {
    if (!organizationId) return Promise.resolve();
    setLoading(true);
    setError(null);
    return getEzCaterIntegration(organizationId)
      .then((data) => {
        setIntegration(data);
        form.setFieldsValue({});
      })
      .catch((e) => {
        captureException(e);
        setError(e?.response?.data?.error || e?.response?.data?.detail || e?.message || 'Failed to load ezCater integration');
        setIntegration(null);
      })
      .finally(() => setLoading(false));
  }, [organizationId, form]);

  useEffect(() => {
    load();
  }, [load]);

  const handleRequestActivation = (isResend = false) => {
    setRequesting(true);
    requestEzCaterActivation(organizationId)
      .then((data) => {
        setIntegration(data);
        if (!isResend) {
          message.success(
            'We\'ve received your request. You\'ll hear back from us in 3–5 business days.',
          );
          return;
        }
        if (data?.resend_reason === 'failure') {
          message.success('Notification email sent again.');
          return;
        }
        message.success('Follow-up sent to our team.');
      })
      .catch((e) => {
        captureException(e);
        message.error(e?.response?.data?.detail || e?.response?.data?.error || 'Failed to request activation');
      })
      .finally(() => setRequesting(false));
  };

  const handleSaveCredentials = (values) => {
    if (!values.api_token && integration?.status !== 'active') {
      message.error('API authorization token is required');
      return;
    }
    if (!values.api_token) {
      message.info('Enter a new token to update credentials.');
      return;
    }
    setSaving(true);
    saveEzCaterCredentials(organizationId, {
      api_token: values.api_token,
    })
      .then((data) => {
        setIntegration(data);
        form.resetFields(['api_token']);
        message.success(
          isActive
            ? 'ezCater credentials updated.'
            : 'ezCater credentials saved. Map locations below to receive orders.',
        );
        load();
      })
      .catch((e) => {
        captureException(e);
        message.error(e?.response?.data?.detail || e?.response?.data?.error || 'Failed to save credentials');
        load();
      })
      .finally(() => setSaving(false));
  };

  const handleTestConnection = () => {
    setTesting(true);
    testEzCaterConnection(organizationId)
      .then((data) => {
        const count = data?.caterer_count ?? 0;
        message.success(`Connection OK — ${count} caterer location(s) accessible.`);
      })
      .catch((e) => {
        captureException(e);
        message.error(e?.response?.data?.detail || e?.response?.data?.error || 'Connection test failed');
      })
      .finally(() => setTesting(false));
  };

  if (!organizationId) {
    return (
      <Alert type="info" showIcon message="Select an organization in the header to manage integrations." />
    );
  }

  const statusKey = integration?.status || 'not_configured';
  const statusMeta = STATUS_CONFIG[statusKey] || STATUS_CONFIG.not_configured;
  const isPending = statusKey === 'pending';
  const isActive = statusKey === 'active';
  const showCredentialForm = Boolean(integration?.can_configure_credentials);
  const canResend = Boolean(integration?.can_resend_notification);
  const resendReason = integration?.resend_reason;
  const notificationFailed = integration?.last_notification_succeeded === false;

  const resendButtonLabel = resendReason === 'follow_up'
    ? 'Send follow-up to our team'
    : 'Resend notification email';

  const formatResendAvailableAt = (value) => {
    if (!value) return null;
    return new Date(value).toLocaleString();
  };

  const mappedLocationRows = (integration?.caterer_mappings || [])
    .filter((row) => row.mapped_restaurant_id)
    .map((row) => {
      const storeLabel = row.store_number
        ? `${row.name || 'Store'} (#${row.store_number})`
        : (row.name || 'Store');
      return (
        <div key={row.uuid}>
          <Text style={{ color: 'rgba(0, 0, 0, 0.65)', fontSize: 12 }}>
            {storeLabel}
            {' → '}
            {formatRestaurantLabel({
              name: row.mapped_restaurant_name,
              id: row.mapped_restaurant_id,
            })}
          </Text>
        </div>
      );
    });

  if (loading) {
    return <Spin />;
  }

  return (
    <Space direction="vertical" size={sectionStackGap} style={{ width: '100%' }}>
      {error && <Alert type="error" showIcon message={error} />}

      <Card
        {...sectionCardProps}
        title={(
          <SectionTitle
            title="ezCater"
            tag={<Tag color={statusMeta.color}>{statusMeta.label}</Tag>}
          />
        )}
      >
        <Text style={{
          color: 'rgba(0, 0, 0, 0.75)', display: 'block', lineHeight: 1.4, marginBottom: 16,
        }}
        >
          {ezCaterDetail(statusKey)}
        </Text>
        <InfoTable
          rows={[
            {
              label: 'Status',
              value: statusMeta.label,
            },
            ...(integration?.requested_at ? [{
              label: 'Requested',
              value: new Date(integration.requested_at).toLocaleString(),
            }] : []),
            {
              label: 'API token',
              value: integration?.has_token_configured
                ? (
                  <Text>
                    Configured
                    {' '}
                    ******
                    <Text strong style={{ fontWeight: 700 }}>{integration.token_last4}</Text>
                  </Text>
                )
                : 'Not set',
            },
            {
              label: 'Mapped locations',
              value: (
                <div>
                  <div>{formatCatererLocationCounts(integration)}</div>
                  {mappedLocationRows}
                </div>
              ),
            },
            {
              label: 'Webhook URL',
              value: (
                <Space wrap>
                  <Text code style={{ wordBreak: 'break-all' }}>{integration?.webhook_url || '—'}</Text>
                  {integration?.webhook_url ? (
                    <Button
                      type="text"
                      size="small"
                      icon={<CopyOutlined />}
                      onClick={() => copyToClipboard(integration?.webhook_url)}
                      aria-label="Copy webhook URL"
                    />
                  ) : null}
                </Space>
              ),
            },
          ]}
        />

        {integration?.last_error && (
          <Alert type="error" showIcon message={integration.last_error} style={{ marginTop: 16 }} />
        )}

        {statusKey === 'not_configured' && integration?.can_request_activation && (
          <div style={{ marginTop: 16 }}>
            <Text style={{
              color: 'rgba(0, 0, 0, 0.65)', display: 'block', fontSize: 12, lineHeight: 1.4, marginBottom: 12,
            }}
            >
              Request activation to start onboarding with ezCater. Initial setup usually takes 3–5 business days.
            </Text>
            <Button type="primary" onClick={() => handleRequestActivation(false)} loading={requesting}>
              Request activation
            </Button>
          </div>
        )}

        {isPending && (
          <Space direction="vertical" size={12} style={{ width: '100%', marginTop: 16 }}>
            {notificationFailed && (
              <Alert
                type="warning"
                showIcon
                message="We could not notify our team"
                description="Please use the button below to resend the notification email."
              />
            )}
            <Alert
              type="info"
              showIcon
              message="Activation pending"
              description={(
                <>
                  {notificationFailed
                    ? 'Your request is saved, but our team may not have been notified yet.'
                    : 'We have been notified and will coordinate with ezCater. This typically takes 3–5 business days.'}
                  {' '}
                  Once you receive API credentials, enter the authorization token below to activate.
                </>
              )}
            />
            {canResend ? (
              <Button onClick={() => handleRequestActivation(true)} loading={requesting}>
                {resendButtonLabel}
              </Button>
            ) : (
              integration?.resend_available_at && (
                <Text style={{ color: 'rgba(0, 0, 0, 0.65)', fontSize: 12 }}>
                  You can send a follow-up after
                  {' '}
                  {formatResendAvailableAt(integration.resend_available_at)}
                  .
                </Text>
              )
            )}
          </Space>
        )}
      </Card>

      {showCredentialForm && (
        <Card
          {...sectionCardProps}
          title={isPending ? 'Activate integration' : 'Credentials'}
        >
          <Text style={{
            color: 'rgba(0, 0, 0, 0.75)', display: 'block', lineHeight: 1.4, marginBottom: 16,
          }}
          >
            Paste the API authorization token from the ezCater Partner Portal.
          </Text>
          <Form
            className="org-settings-form"
            form={form}
            layout="vertical"
            onFinish={handleSaveCredentials}
            style={{ maxWidth: FORM_MAX_WIDTH }}
          >
            <Form.Item
              label="API authorization token"
              name="api_token"
              rules={isActive ? [] : [{ required: true, message: 'Token is required' }]}
              extra={!isActive
                ? 'After activation, map each ezCater store to a Nibble restaurant in Location mapping.'
                : 'Leave blank to keep the current token.'}
              style={formItemStyle}
            >
              <Input.Password
                placeholder={isActive ? 'Leave blank to keep current token' : 'Paste token from ezCater Partner Portal'}
              />
            </Form.Item>
            <Form.Item style={{ marginBottom: 0 }}>
              <Space wrap>
                <Button type="primary" htmlType="submit" loading={saving}>
                  {isActive ? 'Update credentials' : 'Save and activate'}
                </Button>
                {integration?.has_token_configured && (
                  <Button onClick={handleTestConnection} loading={testing}>
                    Test connection
                  </Button>
                )}
              </Space>
            </Form.Item>
          </Form>
        </Card>
      )}

      {isActive && (
        <Card {...sectionCardProps} title="Location mapping">
          <EzCaterLocationMapping
            organizationId={organizationId}
            integration={integration}
            onUpdated={setIntegration}
          />
        </Card>
      )}
    </Space>
  );
}

export default EzCaterIntegration;
